/**
 * Comprehensive validation utilities for form fields
 * Following real-world best practices for input validation
 */

import * as Yup from 'yup';

export interface ValidationResult {
  isValid: boolean;
  errorMessage?: string;
}

/**
 * Validates if a string is not empty or only whitespace
 */
export const validateNotEmpty = (value: string, fieldName: string): ValidationResult => {
  if (!value || value.trim().length === 0) {
    return {
      isValid: false,
      errorMessage: `${fieldName} cannot be empty`
    };
  }
  return { isValid: true };
};

/**
 * Validates if a string contains only whitespace characters
 */
export const validateNotOnlyWhitespace = (value: string, fieldName: string): ValidationResult => {
  if (value && value.trim().length === 0) {
    return {
      isValid: false,
      errorMessage: `${fieldName} cannot contain only spaces`
    };
  }
  return { isValid: true };
};

/**
 * Validates if a string contains only special characters (no letters, numbers, or meaningful content)
 */
export const validateNotOnlySpecialChars = (value: string, fieldName: string): ValidationResult => {
  if (!value) return { isValid: true };
  
  // Remove common punctuation and spaces, check if anything meaningful remains
  const meaningfulContent = value.replace(/[^\w\s\u0600-\u06FF]/g, '').trim();
  if (meaningfulContent.length === 0) {
    return {
      isValid: false,
      errorMessage: `${fieldName} cannot contain only special characters`
    };
  }
  return { isValid: true };
};

/**
 * Validates if a string contains only numbers
 */
export const validateNotOnlyNumbers = (value: string, fieldName: string): ValidationResult => {
  if (!value) return { isValid: true };
  
  if (/^\d+$/.test(value.trim())) {
    return {
      isValid: false,
      errorMessage: `${fieldName} cannot contain only numbers`
    };
  }
  return { isValid: true };
};

/**
 * Validates minimum length
 */
export const validateMinLength = (value: string, minLength: number, fieldName: string): ValidationResult => {
  if (!value) return { isValid: true };
  
  if (value.trim().length < minLength) {
    return {
      isValid: false,
      errorMessage: `${fieldName} must be at least ${minLength} characters long`
    };
  }
  return { isValid: true };
};

/**
 * Validates maximum length
 */
export const validateMaxLength = (value: string, maxLength: number, fieldName: string): ValidationResult => {
  if (!value) return { isValid: true };
  
  if (value.length > maxLength) {
    return {
      isValid: false,
      errorMessage: `${fieldName} cannot exceed ${maxLength} characters`
    };
  }
  return { isValid: true };
};

/**
 * Validates if text contains meaningful content (not just repeated characters)
 */
export const validateMeaningfulContent = (value: string, fieldName: string): ValidationResult => {
  if (!value) return { isValid: true };
  
  const trimmed = value.trim();
  if (trimmed.length === 0) return { isValid: true };
  
  // Check for repeated characters (e.g., "aaaa", "1111", "!!!!")
  const uniqueChars = new Set(trimmed.split(''));
  if (uniqueChars.size === 1 && trimmed.length > 2) {
    return {
      isValid: false,
      errorMessage: `${fieldName} cannot contain only repeated characters`
    };
  }
  
  return { isValid: true };
};

/**
 * Validates Arabic text specifically
 */
export const validateArabicText = (value: string, fieldName: string): ValidationResult => {
  if (!value) return { isValid: true };
  
  // Arabic Unicode range: \u0600-\u06FF
  const arabicRegex = /[\u0600-\u06FF]/;
  const hasArabic = arabicRegex.test(value);
  
  if (!hasArabic) {
    return {
      isValid: false,
      errorMessage: `${fieldName} must contain Arabic text`
    };
  }
  
  return { isValid: true };
};

/**
 * Validates English text specifically
 */
export const validateEnglishText = (value: string, fieldName: string): ValidationResult => {
  if (!value) return { isValid: true };
  
  // English letters and common punctuation
  const englishRegex = /[a-zA-Z]/;
  const hasEnglish = englishRegex.test(value);
  
  if (!hasEnglish) {
    return {
      isValid: false,
      errorMessage: `${fieldName} must contain English text`
    };
  }
  
  return { isValid: true };
};

/**
 * Validates email format
 */
export const validateEmail = (value: string, fieldName: string): ValidationResult => {
  if (!value) return { isValid: true };
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(value)) {
    return {
      isValid: false,
      errorMessage: `${fieldName} must be a valid email address`
    };
  }
  
  return { isValid: true };
};

/**
 * Validates phone number format
 */
export const validatePhoneNumber = (value: string, fieldName: string, options: { minLength?: number; maxLength?: number } = {}): ValidationResult => {
  if (!value) return { isValid: true };
  
  const { minLength = 10, maxLength = 15 } = options;
  
  // Remove all non-digit characters
  const cleaned = value.replace(/\D/g, '');
  
  if (cleaned.length < minLength || cleaned.length > maxLength) {
    return {
      isValid: false,
      errorMessage: `${fieldName} must be between ${minLength} and ${maxLength} digits`
    };
  }
  
  return { isValid: true };
};

/**
 * Validates numeric value
 */
export const validateNumeric = (value: string, fieldName: string, options: { min?: number; max?: number } = {}): ValidationResult => {
  if (!value) return { isValid: true };
  
  const num = parseFloat(value);
  if (isNaN(num)) {
    return {
      isValid: false,
      errorMessage: `${fieldName} must be a valid number`
    };
  }
  
  const { min, max } = options;
  if (min !== undefined && num < min) {
    return {
      isValid: false,
      errorMessage: `${fieldName} must be at least ${min}`
    };
  }
  
  if (max !== undefined && num > max) {
    return {
      isValid: false,
      errorMessage: `${fieldName} cannot exceed ${max}`
    };
  }
  
  return { isValid: true };
};

/**
 * Validates no leading/trailing spaces
 */
export const validateNoLeadingTrailingSpaces = (value: string, fieldName: string): ValidationResult => {
  if (!value) return { isValid: true };
  
  if (value !== value.trim()) {
    return {
      isValid: false,
      errorMessage: `${fieldName} cannot start or end with spaces`
    };
  }
  
  return { isValid: true };
};

/**
 * Validates alphanumeric with spaces
 */
export const validateAlphanumericWithSpaces = (value: string, fieldName: string): ValidationResult => {
  if (!value) return { isValid: true };
  
  const regex = /^[A-Za-z0-9\s]+$/;
  if (!regex.test(value)) {
    return {
      isValid: false,
      errorMessage: `${fieldName} should only contain letters, numbers, and spaces`
    };
  }
  
  return { isValid: true };
};

/**
 * Validates Arabic with numbers and spaces
 */
export const validateArabicWithNumbersAndSpaces = (value: string, fieldName: string): ValidationResult => {
  if (!value) return { isValid: true };
  
  const regex = /^[\u0600-\u06FF0-9\s]+$/;
  if (!regex.test(value)) {
    return {
      isValid: false,
      errorMessage: `${fieldName} should only contain Arabic letters, numbers, and spaces`
    };
  }
  
  return { isValid: true };
};

/**
 * Comprehensive validation for FAQ fields
 */
export const validateFaqField = (
  value: string, 
  fieldName: string, 
  options: {
    minLength?: number;
    maxLength?: number;
    requireArabic?: boolean;
    requireEnglish?: boolean;
  } = {}
): ValidationResult => {
  const { minLength = 3, maxLength = 1000, requireArabic = false, requireEnglish = false } = options;
  
  // Check if empty
  const emptyCheck = validateNotEmpty(value, fieldName);
  if (!emptyCheck.isValid) return emptyCheck;
  
  // Check if only whitespace
  const whitespaceCheck = validateNotOnlyWhitespace(value, fieldName);
  if (!whitespaceCheck.isValid) return whitespaceCheck;
  
  // Check if only special characters
  const specialCharsCheck = validateNotOnlySpecialChars(value, fieldName);
  if (!specialCharsCheck.isValid) return specialCharsCheck;
  
  // Check if only numbers
  const numbersCheck = validateNotOnlyNumbers(value, fieldName);
  if (!numbersCheck.isValid) return numbersCheck;
  
  // Check meaningful content
  const meaningfulCheck = validateMeaningfulContent(value, fieldName);
  if (!meaningfulCheck.isValid) return meaningfulCheck;
  
  // Check minimum length
  const minLengthCheck = validateMinLength(value, minLength, fieldName);
  if (!minLengthCheck.isValid) return minLengthCheck;
  
  // Check maximum length
  const maxLengthCheck = validateMaxLength(value, maxLength, fieldName);
  if (!maxLengthCheck.isValid) return maxLengthCheck;
  
  // Check Arabic requirement
  if (requireArabic) {
    const arabicCheck = validateArabicText(value, fieldName);
    if (!arabicCheck.isValid) return arabicCheck;
  }
  
  // Check English requirement
  if (requireEnglish) {
    const englishCheck = validateEnglishText(value, fieldName);
    if (!englishCheck.isValid) return englishCheck;
  }
  
  return { isValid: true };
};

/**
 * Comprehensive validation for name fields
 */
export const validateNameField = (
  value: string,
  fieldName: string,
  options: {
    minLength?: number;
    maxLength?: number;
    allowArabic?: boolean;
  } = {}
): ValidationResult => {
  const { minLength = 2, maxLength = 100, allowArabic = false } = options;
  
  // Check if empty
  const emptyCheck = validateNotEmpty(value, fieldName);
  if (!emptyCheck.isValid) return emptyCheck;
  
  // Check if only whitespace
  const whitespaceCheck = validateNotOnlyWhitespace(value, fieldName);
  if (!whitespaceCheck.isValid) return whitespaceCheck;
  
  // Check no leading/trailing spaces
  const spacesCheck = validateNoLeadingTrailingSpaces(value, fieldName);
  if (!spacesCheck.isValid) return spacesCheck;
  
  // Check minimum length
  const minLengthCheck = validateMinLength(value, minLength, fieldName);
  if (!minLengthCheck.isValid) return minLengthCheck;
  
  // Check maximum length
  const maxLengthCheck = validateMaxLength(value, maxLength, fieldName);
  if (!maxLengthCheck.isValid) return maxLengthCheck;
  
  // Check meaningful content
  const meaningfulCheck = validateMeaningfulContent(value, fieldName);
  if (!meaningfulCheck.isValid) return meaningfulCheck;
  
  // Check format based on language preference
  if (allowArabic) {
    const arabicCheck = validateArabicWithNumbersAndSpaces(value, fieldName);
    if (!arabicCheck.isValid) return arabicCheck;
  } else {
    const alphanumericCheck = validateAlphanumericWithSpaces(value, fieldName);
    if (!alphanumericCheck.isValid) return alphanumericCheck;
  }
  
  return { isValid: true };
};

/**
 * Validates cancellation policy with comprehensive checks
 */
export const validateCancellationPolicy = (value: string, fieldName: string): ValidationResult => {
  // Check if empty
  const notEmptyResult = validateNotEmpty(value, fieldName);
  if (!notEmptyResult.isValid) {
    return notEmptyResult;
  }

  // Check if only whitespace
  const notOnlyWhitespaceResult = validateNotOnlyWhitespace(value, fieldName);
  if (!notOnlyWhitespaceResult.isValid) {
    return notOnlyWhitespaceResult;
  }

  // Check if only numbers
  const notOnlyNumbersResult = validateNotOnlyNumbers(value, fieldName);
  if (!notOnlyNumbersResult.isValid) {
    return notOnlyNumbersResult;
  }

  // Check if only special characters
  const notOnlySpecialCharsResult = validateNotOnlySpecialChars(value, fieldName);
  if (!notOnlySpecialCharsResult.isValid) {
    return notOnlySpecialCharsResult;
  }

  // Check for meaningful content
  const meaningfulContentResult = validateMeaningfulContent(value, fieldName);
  if (!meaningfulContentResult.isValid) {
    return meaningfulContentResult;
  }

  // Check minimum length (at least 10 characters for meaningful policy)
  const minLengthResult = validateMinLength(value, 10, fieldName);
  if (!minLengthResult.isValid) {
    return minLengthResult;
  }

  // Check maximum length (reasonable limit for policy text)
  const maxLengthResult = validateMaxLength(value, 2000, fieldName);
  if (!maxLengthResult.isValid) {
    return maxLengthResult;
  }

  return { isValid: true };
};

/**
 * Creates a Yup validation schema for FAQ fields
 */
export const createFaqValidationSchema = () => {
  return Yup.object().shape({
    question: Yup.string()
      .required('Question is required')
      .test('comprehensive-validation', 'Invalid question format', function(value: any) {
        if (!value) return true; // Let required handle empty values
        
        const result = validateFaqField(value, 'Question', {
          minLength: 10,
          maxLength: 500,
          requireEnglish: true
        });
        
        if (!result.isValid) {
          return (this as any).createError({ message: result.errorMessage });
        }
        
        return true;
      }),
    
    questionInArabic: Yup.string()
      .required('Question in Arabic is required')
      .test('comprehensive-validation', 'Invalid Arabic question format', function(value: any) {
        if (!value) return true; // Let required handle empty values
        
        const result = validateFaqField(value, 'Question in Arabic', {
          minLength: 10,
          maxLength: 500,
          requireArabic: true
        });
        
        if (!result.isValid) {
          return (this as any).createError({ message: result.errorMessage });
        }
        
        return true;
      }),
    
    answer: Yup.string()
      .required('Answer is required')
      .test('comprehensive-validation', 'Invalid answer format', function(value: any) {
        if (!value) return true; // Let required handle empty values
        
        const result = validateFaqField(value, 'Answer', {
          minLength: 20,
          maxLength: 2000,
          requireEnglish: true
        });
        
        if (!result.isValid) {
          return (this as any).createError({ message: result.errorMessage });
        }
        
        return true;
      }),
    
    answerInArabic: Yup.string()
      .required('Answer in Arabic is required')
      .test('comprehensive-validation', 'Invalid Arabic answer format', function(value: any) {
        if (!value) return true; // Let required handle empty values
        
        const result = validateFaqField(value, 'Answer in Arabic', {
          minLength: 20,
          maxLength: 2000,
          requireArabic: true
        });
        
        if (!result.isValid) {
          return (this as any).createError({ message: result.errorMessage });
        }
        
        return true;
      })
  });
};

/**
 * Creates a Yup validation schema for name fields
 */
export const createNameValidationSchema = (fieldName: string, options: { allowArabic?: boolean; maxLength?: number } = {}) => {
  const { allowArabic = false, maxLength = 100 } = options;
  
  return Yup.string()
    .required(`${fieldName} is required`)
    .test('comprehensive-validation', `Invalid ${fieldName.toLowerCase()} format`, function(value: any) {
      if (!value) return true; // Let required handle empty values
      
      const result = validateNameField(value, fieldName, {
        minLength: 2,
        maxLength,
        allowArabic
      });
      
      if (!result.isValid) {
        return (this as any).createError({ message: result.errorMessage });
      }
      
      return true;
    });
};

/**
 * Creates a Yup validation schema for email fields
 */
export const createEmailValidationSchema = () => {
  return Yup.string()
    .required('Email is required')
    .test('email-validation', 'Invalid email format', function(value: any) {
      if (!value) return true; // Let required handle empty values
      
      const result = validateEmail(value, 'Email');
      if (!result.isValid) {
        return (this as any).createError({ message: result.errorMessage });
      }
      
      return true;
    });
};

/**
 * Creates a Yup validation schema for phone number fields
 */
export const createPhoneValidationSchema = (fieldName: string = 'Phone number', options: { minLength?: number; maxLength?: number } = {}) => {
  const { minLength = 10, maxLength = 15 } = options;
  
  return Yup.string()
    .required(`${fieldName} is required`)
    .test('phone-validation', `Invalid ${fieldName.toLowerCase()} format`, function(value: any) {
      if (!value) return true; // Let required handle empty values
      
      const result = validatePhoneNumber(value, fieldName, { minLength, maxLength });
      if (!result.isValid) {
        return (this as any).createError({ message: result.errorMessage });
      }
      
      return true;
    });
};

/**
 * Creates a Yup validation schema for numeric fields
 */
export const createNumericValidationSchema = (fieldName: string, options: { min?: number; max?: number; required?: boolean } = {}) => {
  const { min, max, required = true } = options;
  
  let schema = Yup.number()
    .typeError(`${fieldName} must be a number`);
  
  if (required) {
    schema = schema.required(`${fieldName} is required`);
  }
  
  if (min !== undefined) {
    schema = schema.min(min, `${fieldName} must be at least ${min}`);
  }
  
  if (max !== undefined) {
    schema = schema.max(max, `${fieldName} cannot exceed ${max}`);
  }
  
  return schema;
};

/**
 * Creates a Yup validation schema for CMS fields
 */
export const createCMSValidationSchema = () => {
  return Yup.object().shape({
    pageTitle: Yup.string()
      .required('Page title is required')
      .test('english-only', 'Page title must contain only English letters', function(value: any) {
        if (!value) return true; // Let required handle empty values
        
        // Check if contains only English characters, numbers, spaces, and basic punctuation
        const englishRegex = /^[A-Za-z0-9\s.,'-]+$/;
        if (!englishRegex.test(value)) {
          return (this as any).createError({ message: 'Page title must contain only English letters' });
        }
        
        return true;
      })
      .test('comprehensive-validation', 'Invalid page title format', function(value: any) {
        if (!value) return true; // Let required handle empty values
        
        const result = validateFaqField(value, 'Page title', {
          minLength: 5,
          maxLength: 100,
          requireEnglish: true
        });
        
        if (!result.isValid) {
          return (this as any).createError({ message: result.errorMessage });
        }
        
        return true;
      }),
    
    pageTitleInArabic: Yup.string()
      .required('Page title in Arabic is required')
      .test('arabic-only', 'Page title in Arabic must contain only Arabic letters', function(value: any) {
        if (!value) return true; // Let required handle empty values
        
        // Check if contains only Arabic characters, spaces, and basic punctuation
        const arabicRegex = /^[\u0600-\u06FF\s.,'-]+$/;
        if (!arabicRegex.test(value)) {
          return (this as any).createError({ message: 'Page title in Arabic must contain only Arabic letters' });
        }
        
        return true;
      })
      .test('comprehensive-validation', 'Invalid Arabic page title format', function(value: any) {
        if (!value) return true; // Let required handle empty values
        
        const result = validateFaqField(value, 'Page title in Arabic', {
          minLength: 5,
          maxLength: 100,
          requireArabic: true
        });
        
        if (!result.isValid) {
          return (this as any).createError({ message: result.errorMessage });
        }
        
        return true;
      }),
    
    pageDescription: Yup.string()
      .required('Page description is required')
      .test('comprehensive-validation', 'Invalid page description format', function(value: any) {
        if (!value) return true; // Let required handle empty values
        
        // Strip HTML tags from rich text content for validation checks
        const textContent = value.replace(/<[^>]*>/g, '');
        
        const result = validateFaqField(textContent, 'Page description', {
          minLength: 20,
          maxLength: 5000,
          requireEnglish: true
        });
        
        if (!result.isValid) {
          return (this as any).createError({ message: result.errorMessage });
        }
        
        return true;
      }),
    
    pageDescriptionInArabic: Yup.string()
      .required('Page description in Arabic is required')
      .test('comprehensive-validation', 'Invalid Arabic page description format', function(value: any) {
        if (!value) return true; // Let required handle empty values
        
        // Strip HTML tags from rich text content for validation checks
        const textContent = value.replace(/<[^>]*>/g, '');
        
        const result = validateFaqField(textContent, 'Page description in Arabic', {
          minLength: 20,
          maxLength: 5000,
          requireArabic: true
        });
        
        if (!result.isValid) {
          return (this as any).createError({ message: result.errorMessage });
        }
        
        return true;
      })
  });
}; 

/**
 * Creates a Yup validation schema for cancellation policy
 */
export const createCancellationPolicyValidationSchema = (fieldName: string = 'Cancellation Policy') => {
  return Yup.string()
    .required(`${fieldName} is required`)
    .test('comprehensive-validation', 'Invalid cancellation policy format', function(value: any) {
      if (!value) return true; // Let required handle empty values
      
      const result = validateCancellationPolicy(value, fieldName);
      
      if (!result.isValid) {
        return (this as any).createError({ message: result.errorMessage });
      }
      
      return true;
    });
}; 

/**
 * Creates a Yup validation schema for short text fields (like subject lines)
 */
export const createShortTextValidationSchema = (fieldName: string = 'Field', minLength: number = 3) => {
  return Yup.string()
    .required(`${fieldName} is required`)
    .test('comprehensive-validation', `Invalid ${fieldName.toLowerCase()} format`, function(value: any) {
      if (!value) return true; // Let required handle empty values
      
      // Check if empty
      if (!value || value.trim().length === 0) {
        return (this as any).createError({ message: `${fieldName} cannot be empty` });
      }

      // Check if only whitespace
      if (value.trim().length === 0) {
        return (this as any).createError({ message: `${fieldName} cannot contain only spaces` });
      }

      // Check if only numbers
      if (/^\d+$/.test(value.trim())) {
        return (this as any).createError({ message: `${fieldName} cannot contain only numbers` });
      }

      // Check if only special characters
      const meaningfulContent = value.replace(/[^\w\s\u0600-\u06FF]/g, '').trim();
      if (meaningfulContent.length === 0) {
        return (this as any).createError({ message: `${fieldName} cannot contain only special characters` });
      }

      // Check for meaningful content (not just repeated characters)
      const uniqueChars = new Set(value.trim().split(''));
      if (uniqueChars.size === 1 && value.trim().length > 2) {
        return (this as any).createError({ message: `${fieldName} cannot contain only repeated characters` });
      }

      // Check minimum length
      if (value.trim().length < minLength) {
        return (this as any).createError({ message: `${fieldName} must be at least ${minLength} characters long` });
      }

      // Check maximum length (reasonable limit for short text)
      if (value.length > 200) {
        return (this as any).createError({ message: `${fieldName} cannot exceed 200 characters` });
      }

      return true;
    });
}; 

const COUNTRY_CODES = [
  '+962', '+971', '+966', '+965', '+973', '+974', '+968', '+20', '+961', '+964', '+92', '+1', '+44', '+33', '+49', '+81', '+86', '+91'
];

export const splitPhoneNumber = (fullNumber?: string) => {
  if (!fullNumber) return { countryCode: '', phone: '' };
  const code = COUNTRY_CODES.sort((a, b) => b.length - a.length)
    .find(code => fullNumber.startsWith(code));
  if (code) {
    return {
      countryCode: code,
      phone: fullNumber.slice(code.length)
    };
  }
  return { countryCode: '', phone: fullNumber };
}